import { useState, useEffect, useRef, useMemo } from 'react';
import { isValid } from 'date-fns';
import { Flexbox } from '@morressier/ts';
import { faSquareExclamation } from '@fortawesome/pro-solid-svg-icons';
import DatePickerInput from './DatePickerInput';
import MonthPicker from './MonthPicker';
import { HelperText, ErrorIcon, ErrorText } from '../Input/style';
import { Container } from './style';

interface IDatePickerProps {
  onChange: (date: string) => void;
  value?: string;
  type: 'date' | 'month';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  placement?: 'top' | 'bottom';
}

const DatePicker = (props: IDatePickerProps) => {
  const {
    value,
    onChange,
    type,
    placeholder,
    disabled,
    className,
    error,
    placement
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const inputContainerRef = useRef<HTMLDivElement>(null);
  const monthPickerRef = useRef<HTMLDivElement>(null);

  const date = useMemo(() => {
    const dateObj = new Date(value || '');
    if (isValid(dateObj)) {
      return {
        ...(type === 'date' ? { day: dateObj.getDate() } : {}),
        month: dateObj.getMonth(),
        year: dateObj.getFullYear()
      };
    }
    return {};
  }, [value]);

  const openPicker = () => setIsOpen(true);

  const closePicker = () => {
    setIsOpen(false);
    const svgRef = inputContainerRef.current?.children[1] as SVGElement;
    svgRef?.focus();
  };

  const onDateChange = (newDate: {
    day?: number;
    month?: number;
    year?: number;
  }) => {
    const updatedDate = { ...date, ...newDate };
    const { day, month, year } = updatedDate;
    if (month !== undefined && year) {
      const dayString = day && day < 10 ? `0${day}` : day;
      const realMonth = month + 1;
      onChange(
        `${year}-${realMonth < 10 ? `0${realMonth}` : realMonth}${
          day ? dayString : ''
        }`
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !inputContainerRef.current?.contains(event.target as Node) &&
        !monthPickerRef.current?.contains(event.target as Node) &&
        isOpen
      ) {
        closePicker();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Container className={className}>
      <DatePickerInput
        openPicker={openPicker}
        selectedDate={date}
        onDateChange={onDateChange}
        ref={inputContainerRef}
        placeholder={placeholder}
        type={type}
        disabled={disabled}
        error={error}
      />

      {type === 'month' && (
        <MonthPicker
          isOpen={isOpen}
          onClose={closePicker}
          selectedDate={date}
          onDateChange={onDateChange}
          ref={monthPickerRef}
          placement={placement}
        />
      )}
      {Boolean(error) && (
        <HelperText>
          <Flexbox alignItems="center" gap="4px">
            <ErrorIcon icon={faSquareExclamation} />
            <ErrorText>{error}</ErrorText>
          </Flexbox>
        </HelperText>
      )}
    </Container>
  );
};

export default DatePicker;
